/**
 * The Arrears Backlog chart's drill-down: the filters a bar click opens.
 *
 * CR-8236: each bar holds today's overdue open items aged into weekly bands
 * (backend ``charts_arrears.arrears_backlog_weeks``): week k holds items
 * 7(k-1)..7k-1 days overdue and 12+ everything 77+ days overdue, however far
 * back. The drill used to rebuild those windows in the browser — 8 days wide,
 * on the browser's UTC date — and the 12+ drill kept the dashboard's start
 * date, so it dropped every item due before the period and its total fell
 * short of the bar. The backend now sends each bar's own window
 * (``charts.py`` ``arrearsBacklog``, from the tenant's today); an entry cached
 * before it did falls back to the same arithmetic on the local date.
 */

/** One Arrears Backlog bar, as the dashboard payload carries it. */
export interface ArrearsBacklogBar {
  /** "1".."11" or "12+". */
  week: string
  count: number
  /** CR-7714: null when no rate converts the bar's money. */
  value: number | null
  /** CR-7714 BL-15: items in `count` with no value (no converted price or no rate). */
  value_missing?: number
  /** Inclusive due-date window start (YYYY-MM-DD); null for 12+. */
  date_from?: string | null
  /** Inclusive due-date window end (YYYY-MM-DD). */
  date_to?: string
}

const localIso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const daysAgo = (today: Date, days: number) => {
  const d = new Date(today)
  d.setDate(today.getDate() - days)
  return d
}

/**
 * The drill-down filters for a clicked Arrears Backlog bar.
 *
 * @param bar - The clicked bar, with its window when the backend sent one
 * @param drilldownFilters - The Default tab's drill filters (dashboard scope)
 * @param today - The local date the fallback ages from (injectable for tests)
 * @returns The filters for the `arrears` drill, or null for a label that is not a week
 */
export function arrearsBacklogDrillFilters(
  bar: ArrearsBacklogBar,
  drilldownFilters: Record<string, string>,
  today: Date = new Date(),
): Record<string, string> | null {
  const week = bar.week === '12+' ? 12 : parseInt(bar.week, 10)
  if (isNaN(week)) return null

  let dateTo = bar.date_to
  let dateFrom = bar.date_from ?? undefined
  if (!dateTo) {
    dateTo = localIso(daysAgo(today, Math.max(7 * (week - 1), 1)))
    dateFrom = week >= 12 ? undefined : localIso(daysAgo(today, 7 * week - 1))
  }

  const filters: Record<string, string> = {
    ...drilldownFilters,
    date_to: dateTo,
    // CR-7270: these bars are TODAY'S open-overdue stock aged in weekly
    // bands, built unconditionally in both modes, so they do not move with
    // the Historical/Live switch and their drill must not either. A week
    // window ending on the 1st is indistinguishable by its dates from a
    // month-end question, and in Historical it was answered with the whole
    // previous month's frozen cohort under a "Week 2" heading.
    data_mode: 'live',
  }
  // 12+ is open-ended: the dashboard's own start date must not bound it.
  delete filters['date_from']
  if (dateFrom) filters['date_from'] = dateFrom
  return filters
}
